import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import {
  fetchViews,
  createView,
  updateView,
  deleteView,
} from '@/api/columnViews'
import type { UserColumnViewRead, UserColumnViewUpdate } from '@/types/dag'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

export function useColumnViews() {
  return useQuery({
    queryKey: queryKeys.columnViews.all,
    queryFn: fetchViews,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateView() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createView,
    onSuccess: (data: UserColumnViewRead) => {
      queryClient.setQueryData<UserColumnViewRead[]>(
        queryKeys.columnViews.all,
        existing => (existing ? [...existing, data] : [data])
      )
      toast.success('View saved')
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to save view'))
    },
  })
}

export function useUpdateView() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      viewId,
      payload,
    }: {
      viewId: string
      payload: UserColumnViewUpdate
    }) => updateView(viewId, payload),
    onSuccess: (data: UserColumnViewRead) => {
      queryClient.setQueryData<UserColumnViewRead[]>(
        queryKeys.columnViews.all,
        existing => existing?.map(v => (v.id === data.id ? data : v))
      )
      toast.success('View updated')
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to update view'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.columnViews.all })
    },
  })
}

export function useDeleteView() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (viewId: string) => deleteView(viewId),
    onSuccess: (_data, viewId) => {
      queryClient.setQueryData<UserColumnViewRead[]>(
        queryKeys.columnViews.all,
        existing => existing?.filter(v => v.id !== viewId)
      )
      toast.success('View deleted')
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, 'Failed to delete view'))
    },
  })
}
